/* eslint-disable react/no-array-index-key */
/* eslint-disable no-cond-assign */
/* eslint-disable no-continue */
import useCheckIfPrinting from 'apps/placeme/src/customHooks/useCheckIfPrinting'
import React, { ReactNode, useMemo } from 'react'
import styled, { css } from 'styled-components'

const SummaryMarkdown = styled.div<{$isPrinting?: boolean}>(({ $isPrinting = false }) => css`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  h1,
  h2,
  h3,
  p,
  ul {
    margin: 0;

  ${$isPrinting && css`
      font-size: 10px;
      line-height: 14px;
  `}  
  }

  ul {
    padding-left: 1.25rem;
  }
`)

interface MarkdownRendererProps {
  markdown: string
}

interface MarkdownHeadingBlock {
  type: 'heading'
  level: 1 | 2 | 3
  content: string
}

interface MarkdownParagraphBlock {
  type: 'paragraph'
  content: string
}

interface MarkdownListBlock {
  type: 'list'
  items: string[]
}

type MarkdownBlock = MarkdownHeadingBlock | MarkdownParagraphBlock | MarkdownListBlock

function parseMarkdownBlocks(markdown: string): MarkdownBlock[] {
  const normalized = markdown.replace(/\r\n?/g, '\n').trim()
  if (!normalized) return []

  const blocks: MarkdownBlock[] = []
  const lines = normalized.split('\n')
  let paragraphLines: string[] = []
  let listItems: string[] | null = null

  const flushParagraph = () => {
    if (!paragraphLines.length) return

    blocks.push({
      type: 'paragraph',
      content: paragraphLines.join('\n'),
    })
    paragraphLines = []
  }

  const flushList = () => {
    if (!listItems?.length) return

    blocks.push({
      type: 'list',
      items: listItems,
    })
    listItems = null
  }

  for (const line of lines) {
    const trimmed = line.trim()

    if (!trimmed) {
      flushParagraph()
      flushList()
      continue
    }

    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/)
    if (headingMatch) {
      flushParagraph()
      flushList()

      blocks.push({
        type: 'heading',
        level: headingMatch[1].length as 1 | 2 | 3,
        content: headingMatch[2],
      })
      continue
    }

    const listMatch = trimmed.match(/^[-*]\s+(.+)$/)
    if (listMatch) {
      flushParagraph()
      listItems = listItems ?? []
      listItems.push(listMatch[1])
      continue
    }

    if (listItems?.length) {
      flushList()
    }

    paragraphLines.push(trimmed)
  }

  flushParagraph()
  flushList()

  return blocks
}

function renderInlineTokens(text: string, keyPrefix: string): ReactNode[] {
  const tokens: ReactNode[] = []
  const pattern = /\*\*(.+?)\*\*|\*(.+?)\*|\[([^\]]+)\]\(([^)]+)\)/g
  let cursor = 0
  let match: RegExpExecArray | null
  let tokenIndex = 0

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) {
      tokens.push(text.slice(cursor, match.index))
    }

    if (match[1]) {
      tokens.push(<strong key={`${keyPrefix}-bold-${tokenIndex}`}>{match[1]}</strong>)
    } else if (match[2]) {
      tokens.push(<em key={`${keyPrefix}-italic-${tokenIndex}`}>{match[2]}</em>)
    } else if (match[3] && match[4]) {
      tokens.push((
        <a
          key={`${keyPrefix}-link-${tokenIndex}`}
          href={match[4]}
        >
          {match[3]}
        </a>
      ))
    }

    cursor = pattern.lastIndex
    tokenIndex += 1
  }

  if (cursor < text.length) {
    tokens.push(text.slice(cursor))
  }

  return tokens
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const lines = text.split('\n')

  return lines.flatMap((line, index) => {
    const lineNodes = renderInlineTokens(line, `${keyPrefix}-line-${index}`)
    if (index === lines.length - 1) return lineNodes

    return [...lineNodes, <br key={`${keyPrefix}-br-${index}`} />]
  })
}

function renderMarkdown(blocks: MarkdownBlock[]): ReactNode {
  return blocks.map((block, index) => {
    const keyPrefix = `md-${index}`

    if (block.type === 'heading') {
      const HeadingTag = `h${block.level}` as keyof JSX.IntrinsicElements
      return (
        <HeadingTag key={keyPrefix}>
          {renderInline(block.content, keyPrefix)}
        </HeadingTag>
      )
    }

    if (block.type === 'list') {
      return (
        <ul key={keyPrefix}>
          {block.items.map((item, itemIndex) => (
            <li key={`${keyPrefix}-item-${itemIndex}`}>
              {renderInline(item, `${keyPrefix}-item-${itemIndex}`)}
            </li>
          ))}
        </ul>
      )
    }

    return (
      <p key={keyPrefix}>
        {renderInline(block.content, keyPrefix)}
      </p>
    )
  })
}

export function MarkdownRenderer({ markdown }: MarkdownRendererProps): JSX.Element | null {
  const blocks = useMemo(() => parseMarkdownBlocks(markdown), [markdown])
  const { isPrinting } = useCheckIfPrinting()

  if (!blocks.length) return null

  return (
    <SummaryMarkdown $isPrinting={isPrinting}>
      {renderMarkdown(blocks)}
    </SummaryMarkdown>
  )
}
